import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { ButtonRadioInput } from '@/components/form/fields/radio/ButtonRadioInput';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Form } from '@/components/form/Form';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { Product } from '@/services/api/contracts/catalog/types';
import { useBundles } from '@/services/api/hooks/bundles';
import { useCompany } from '@/services/api/hooks/companies';
import { useSubmitStockProvisionRequest } from '@/services/api/hooks/management';
import { useSelectedProducts } from '@/services/catalogue/useCustomerCatalogueService';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import StockProvisionStandarisedProductSelection from './StockProvisionStandarisedProductSelection';

export interface StandardisedProduct {
  product: Product;
  count: number;
}

const FormFields = z.object({
  customerName: z.string().nonempty(),
  bundle: z.custom<Property>().optional(),
  userFullName: z.string().nonempty(),
  userAddress: z.string().nonempty(),
  workEmailAddress: z.string().email().nonempty(),
  personalEmailAddress: z.string().optional(),
  mobileNumber: z.string().nonempty(),
  notes: z.string().optional()
});

type FormFields = z.infer<typeof FormFields>;

enum FilterType {
  bundles = 'bundles',
  standardised = 'standardised'
}

export const StockProvisionPage: FC = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: 'Management',
      to: ''
    },
    {
      name: 'Deploy Inventory',
      to: ''
    }
  ];

  const { partnerId, companyId } = useParams();

  if (!(partnerId && companyId)) throw new Error('Missing params');
  const [company] = useCompany(partnerId, companyId);

  const methods = useForm<FormFields>({
    resolver: zodResolver(FormFields),
    defaultValues: {
      customerName: company.name
    }
  });

  const navigate = useNavigate();
  const routePrefix = useCustomerRoutePrefix();

  const selectedProducts = useSelectedProducts();

  const [standardisedProducts, setStandardisedProducts] = useState<StandardisedProduct[]>(
    selectedProducts.map(product => {
      return {
        product,
        count: 0
      };
    })
  );

  const onStandardisedProductChange = (productId: string, count: number) => {
    const product = standardisedProducts.find(product => product.product.id === productId);
    if (!product) throw new Error('Failed product lookup');

    product.count = count;

    setStandardisedProducts([...standardisedProducts]);
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const submitRequest = useSubmitStockProvisionRequest();

  const onSubmit = async (data: FormFields) => {
    setLoading(true);
    setErrorMessage('');

    const result = await submitRequest.mutateAsync({
      customerName: data.customerName,
      userFullName: data.userFullName,
      userAddress: data.userAddress,
      workEmailAddress: data.workEmailAddress,
      mobileNumber: data.mobileNumber,
      personalEmailAddress: data.personalEmailAddress,
      notes: data.notes,
      bundle: data.bundle?.name,
      customProducts: standardisedProducts.map(p => ({
        name: p.product.name,
        quantity: p.count,
        sku: p.product.productId
      }))
    });

    if (result.status === 'Message sent.') {
      navigate(`${routePrefix}/management/success`);
    } else {
      setErrorMessage('Unable to submit request.');
      setLoading(false);
    }
  };

  const [bundleList] = useBundles(partnerId, companyId);
  const bundleOptions = useMemo(() => {
    return bundleList.map(b => ({ id: b.id, name: b.name }));
  }, [bundleList]);

  const [filterType, setFilterType] = useState(FilterType.bundles);

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Deploy Inventory</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto p-10">
        <Form methods={methods} onSubmit={onSubmit} className="space-y-5">
          <TextField id="customerName" label="Customer Name" readonly />
          <ButtonRadioInput
            id="type"
            options={[
              {
                id: FilterType.bundles,
                label: <span>Bundled Products</span>
              },
              {
                id: FilterType.standardised,
                label: <span>Standardised Products</span>
              }
            ]}
            onChange={value => {
              setFilterType(value as FilterType);
            }}
            value={filterType}
          />
          {filterType === FilterType.bundles && (
            <SelectField id="bundle" label="Bundle Name" options={bundleOptions} placeholder="Please select" />
          )}
          {filterType === FilterType.standardised && (
            <StockProvisionStandarisedProductSelection
              products={standardisedProducts}
              onChangeCallback={onStandardisedProductChange}
            />
          )}
          <TextField id="userFullName" label="User's Full Name" />
          <TextAreaField id="userAddress" label="User's Address" placeholder="Full address for delivery" />
          <TextField id="workEmailAddress" label="Work Email" />
          <TextField id="personalEmailAddress" label="Personal Email" />
          <TextField id="mobileNumber" label="Mobile Number" />
          <TextAreaField id="notes" label="Notes / Special Requirements" />
          {errorMessage && <p className="text-red-500">{errorMessage}</p>}
          <Button variant={ButtonVariant.Primary} type="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </div>
    </>
  );
};
